/**
 * Customer "Book a Room" (Phase 2). Pick dates + guests -> see available room types with nightly
 * price + stay total -> book a stay (status "requested", pay at hotel). Also lists my stays.
 * Route: /customer/organizers/:organizerId/branches/:branchId/rooms
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, BedDouble, Users, CalendarDays, Check, Loader2, ConciergeBell } from 'lucide-react';
import { apiService } from '@/services/api';
import { subscribeToChannel } from '@/services/ablyRealtime';
import { getUserIdFromToken } from '@/utils/jwt';
import Pagination from '@/components/common/Pagination/Pagination';
import RoomIllustration from '@/components/common/RoomIllustration';
import RoomServicePanel from './RoomServicePanel';

const ROOM_PAGE_SIZE = 6;
const ACTIVE_STATUSES = ['requested', 'confirmed', 'checked_in'];

interface AvailItem {
  roomTypeId: string; name: string; description: string | null; bedType: string | null;
  maxOccupancy: number; amenities: string[] | null; images: string[] | null;
  pricePerNight: number; currency: string; availableCount: number; nights: number; total: number; fitsGuests: boolean;
}
interface Stay {
  id: string; roomTypeName: string | null; roomNumber: string | null; checkInDate: string; checkOutDate: string;
  nights: number; numberOfGuests: number; status: string; totalAmount: number; currency: string;
  paymentStatus: string; balanceDue: number; confirmationCode: string | null; unreadChat?: number;
}

const sym = (c: string) => (c === 'INR' ? '₹' : c === 'AED' ? 'AED ' : c === 'USD' ? '$' : c + ' ');
const money = (n: number, c: string) => `${sym(c)}${Number(n).toLocaleString('en-IN')}`;
const iso = (d: Date) => d.toISOString().slice(0, 10);
const fmt = (d: string) => { try { return new Date(d + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }); } catch { return d; } };
const STATUS_LABEL: Record<string, string> = { requested: 'Requested', confirmed: 'Confirmed', checked_in: 'Checked in', checked_out: 'Completed', cancelled: 'Cancelled', no_show: 'No-show' };

const RoomBooking: React.FC = () => {
  const { organizerId, branchId } = useParams<{ organizerId: string; branchId: string }>();
  const navigate = useNavigate();

  const today = new Date();
  const tomorrow = new Date(Date.now() + 86400000);
  const [checkIn, setCheckIn] = useState(iso(today));
  const [checkOut, setCheckOut] = useState(iso(tomorrow));
  const [guests, setGuests] = useState(1);
  const [branchName, setBranchName] = useState('');

  const [items, setItems] = useState<AvailItem[] | null>(null);
  const [nights, setNights] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<string | null>(null); // which item is being booked
  const [confirmed, setConfirmed] = useState<Stay | null>(null);

  const [tab, setTab] = useState<'search' | 'stays' | 'history'>('search');
  const [stays, setStays] = useState<Stay[]>([]);
  const [servicePanel, setServicePanel] = useState<{ bookingId: string; roomLabel: string; canShare: boolean; unreadChat: number } | null>(null);
  const [searchPage, setSearchPage] = useState(1);
  const [stayPage, setStayPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);

  useEffect(() => {
    if (!branchId) return;
    apiService.getBranchDetails(branchId).then((r: any) => setBranchName(r?.data?.name || r?.data?.branchName || '')).catch(() => {});
  }, [branchId]);

  const search = useCallback(async () => {
    if (!branchId) return;
    setLoading(true); setError(null); setConfirmed(null);
    try {
      const res: any = await apiService.getRoomAvailability(branchId, checkIn, checkOut, guests);
      setItems(Array.isArray(res?.items) ? res.items : []);
      setNights(res?.nights || 1);
      setSearchPage(1);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not check availability'); setItems([]); }
    finally { setLoading(false); }
  }, [branchId, checkIn, checkOut, guests]);

  const book = async (item: AvailItem) => {
    if (!branchId) return;
    setBookingId(item.roomTypeId); setError(null);
    try {
      const res: any = await apiService.createRoomBooking({ branchId, roomTypeId: item.roomTypeId, checkInDate: checkIn, checkOutDate: checkOut, numberOfGuests: guests });
      setConfirmed(res?.booking || null);
    } catch (e) { setError(e instanceof Error ? e.message : 'Booking failed'); }
    finally { setBookingId(null); }
  };

  const loadStays = useCallback(async () => {
    setLoading(true); setError(null);
    try { const res: any = await apiService.getMyRoomBookings(); setStays(Array.isArray(res?.bookings) ? res.bookings : []); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not load your stays'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (tab === 'stays' || tab === 'history') void loadStays(); }, [tab, loadStays]);
  // Live: refresh my stays when the hotel confirms / checks me in / out (Ably).
  useEffect(() => {
    if (tab !== 'stays' && tab !== 'history') return;
    const { accessToken } = apiService.getTokens();
    const userId = accessToken ? getUserIdFromToken(accessToken) : null;
    if (!userId) return;
    const unsub = subscribeToChannel(`user:${userId}`, ['room_booking:updated', 'room_chat:new'], () => { void loadStays(); });
    return () => { try { unsub(); } catch { /* noop */ } };
  }, [tab, loadStays]);

  const cancelStay = async (id: string) => {
    if (!window.confirm('Cancel this stay?')) return;
    try { await apiService.cancelRoomBooking(id); await loadStays(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Cancel failed'); }
  };

  const activeStays = stays.filter((b) => ACTIVE_STATUSES.includes(b.status));
  const pastStays = stays.filter((b) => !ACTIVE_STATUSES.includes(b.status));
  const pagedItems = items ? items.slice((searchPage - 1) * ROOM_PAGE_SIZE, searchPage * ROOM_PAGE_SIZE) : null;

  const renderStayList = (list: Stay[], page: number, setPage: (p: number) => void, emptyText: string) => {
    if (loading) return <p style={s.note}>Loading…</p>;
    if (list.length === 0) return <p style={s.note}>{emptyText}</p>;
    const paged = list.slice((page - 1) * ROOM_PAGE_SIZE, page * ROOM_PAGE_SIZE);
    return (
      <>
        <div style={s.list}>
          {paged.map((b) => (
            <div key={b.id} style={s.stayCard}>
              <div style={s.stayTop}>
                <div style={s.roomName}>{b.roomTypeName || 'Room'}{b.roomNumber ? ` · Room ${b.roomNumber}` : ''}</div>
                <span style={{ ...s.statusBadge, ...(statusStyle(b.status)) }}>{STATUS_LABEL[b.status] || b.status}</span>
              </div>
              <div style={s.stayMeta}>{fmt(b.checkInDate)} → {fmt(b.checkOutDate)} · {b.nights}n · {b.numberOfGuests} guest(s)</div>
              <div style={s.stayMeta}>{money(b.totalAmount, b.currency)} · {b.paymentStatus === 'paid' ? 'Paid' : b.balanceDue > 0 ? `Balance ${money(b.balanceDue, b.currency)} (pay at hotel)` : 'Pay at hotel'}</div>
              {b.confirmationCode && <div style={s.code}>#{b.confirmationCode}</div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                {['confirmed', 'checked_in'].includes(b.status) && (
                  <button style={{ ...s.serviceBtn, position: 'relative' }} onClick={() => setServicePanel({ bookingId: b.id, roomLabel: b.roomNumber ? `Room ${b.roomNumber}` : (b.roomTypeName || 'Your room'), canShare: b.status === 'checked_in', unreadChat: b.unreadChat ?? 0 })}>
                    <ConciergeBell size={15} /> Room services
                    {(b.unreadChat ?? 0) > 0 && <span style={s.unreadDot}>{(b.unreadChat ?? 0) > 9 ? '9+' : b.unreadChat}</span>}
                  </button>
                )}
                {['requested', 'confirmed'].includes(b.status) && <button style={s.cancelBtn} onClick={() => void cancelStay(b.id)}>Cancel</button>}
              </div>
            </div>
          ))}
        </div>
        <Pagination page={page} total={list.length} pageSize={ROOM_PAGE_SIZE} onChange={setPage} />
      </>
    );
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.back} onClick={() => navigate(`/customer/organizers/${organizerId}/branches/${branchId}`)}><ArrowLeft size={18} /></button>
        <div>
          <div style={s.title}><BedDouble size={18} /> Book a room</div>
          {branchName && <div style={s.sub}>{branchName}</div>}
        </div>
      </div>

      <div style={s.tabs}>
        <button style={{ ...s.tab, ...(tab === 'search' ? s.tabActive : {}) }} onClick={() => setTab('search')}>Find a room</button>
        <button style={{ ...s.tab, ...(tab === 'stays' ? s.tabActive : {}) }} onClick={() => setTab('stays')}>My stays</button>
        <button style={{ ...s.tab, ...(tab === 'history' ? s.tabActive : {}) }} onClick={() => setTab('history')}>Previous history</button>
      </div>

      {error && <div style={s.error}>{error}</div>}

      {tab === 'search' ? (
        confirmed ? (
          <div style={s.confirmCard}>
            <div style={s.confirmIcon}><Check size={30} /></div>
            <h3 style={{ margin: '10px 0 4px' }}>Stay requested!</h3>
            <p style={s.confirmSub}>The hotel will confirm your reservation shortly. Payment is collected at the hotel.</p>
            <div style={s.confirmRow}><span>Confirmation</span><b>{confirmed.confirmationCode}</b></div>
            <div style={s.confirmRow}><span>Room</span><b>{confirmed.roomTypeName}</b></div>
            <div style={s.confirmRow}><span>Dates</span><b>{fmt(confirmed.checkInDate)} → {fmt(confirmed.checkOutDate)} ({confirmed.nights}n)</b></div>
            <div style={s.confirmRow}><span>Total</span><b>{money(confirmed.totalAmount, confirmed.currency)}</b></div>
            <button style={s.primaryBtn} onClick={() => { setConfirmed(null); setTab('stays'); }}>View my stays</button>
          </div>
        ) : (
          <>
            <div style={s.searchCard}>
              <div style={s.field}><label style={s.label}><CalendarDays size={13} /> Check-in</label>
                <input type="date" style={s.input} value={checkIn} min={iso(today)} onChange={(e) => setCheckIn(e.target.value)} /></div>
              <div style={s.field}><label style={s.label}><CalendarDays size={13} /> Check-out</label>
                <input type="date" style={s.input} value={checkOut} min={checkIn} onChange={(e) => setCheckOut(e.target.value)} /></div>
              <div style={{ ...s.field, gridColumn: '1 / -1' }}><label style={s.label}><Users size={13} /> Guests</label>
                <input type="number" min={1} style={s.input} value={guests} onChange={(e) => setGuests(Math.max(1, Number(e.target.value)))} /></div>
              <button style={s.primaryBtn} onClick={() => void search()} disabled={loading}>{loading ? <Loader2 size={16} className="spin" /> : 'Search'}</button>
            </div>

            {items === null ? (
              <p style={s.note}>Pick your dates and search for available rooms.</p>
            ) : items.length === 0 ? (
              <p style={s.note}>This venue has no rooms set up yet.</p>
            ) : (
              <>
              <div style={s.list}>
                {(pagedItems || []).map((it) => {
                  const soldOut = it.availableCount < 1;
                  return (
                    <div key={it.roomTypeId} style={{ ...s.roomCard, ...(soldOut ? s.soldOut : {}) }}>
                      {it.images && it.images[0]
                        ? <img src={it.images[0]} alt={it.name} style={s.roomImg} />
                        : <div style={s.roomImg}><RoomIllustration style={{ width: '100%', height: '100%', display: 'block' }} /></div>}
                      <div style={s.roomBody}>
                        <div style={s.roomName}>{it.name}</div>
                        <div style={s.roomMeta}>Up to {it.maxOccupancy} guests{it.bedType ? ` · ${it.bedType}` : ''}</div>
                        {it.amenities && it.amenities.length > 0 && <div style={s.amenities}>{it.amenities.slice(0, 5).join(' · ')}</div>}
                        <div style={s.priceRow}>
                          <div><b style={s.price}>{money(it.pricePerNight, it.currency)}</b><span style={s.perNight}>/night</span>
                            <div style={s.totalLine}>{money(it.total, it.currency)} for {nights} night{nights > 1 ? 's' : ''}</div></div>
                          {soldOut ? <span style={s.soldBadge}>Sold out</span>
                            : !it.fitsGuests ? <span style={s.soldBadge}>Too many guests</span>
                            : <button style={s.bookBtn} onClick={() => void book(it)} disabled={bookingId === it.roomTypeId}>{bookingId === it.roomTypeId ? '…' : 'Book'}</button>}
                        </div>
                        {!soldOut && it.availableCount <= 3 && <div style={s.fewLeft}>Only {it.availableCount} left</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
              <Pagination page={searchPage} total={items.length} pageSize={ROOM_PAGE_SIZE} onChange={setSearchPage} />
              </>
            )}
          </>
        )
      ) : tab === 'stays' ? (
        renderStayList(activeStays, stayPage, setStayPage, 'You have no active stays. Book a room from “Find a room”.')
      ) : (
        renderStayList(pastStays, historyPage, setHistoryPage, 'No previous stays yet.')
      )}

      {servicePanel && (
        <RoomServicePanel bookingId={servicePanel.bookingId} branchId={branchId} roomLabel={servicePanel.roomLabel} accessLevel="full" canShare={servicePanel.canShare} unreadChat={servicePanel.unreadChat} onClose={() => { setServicePanel(null); void loadStays(); }} />
      )}
      <style>{`.spin{animation:spin 0.9s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

function statusStyle(status: string): React.CSSProperties {
  switch (status) {
    case 'confirmed': return { background: '#dbeafe', color: '#1d4ed8' };
    case 'checked_in': return { background: '#dcfce7', color: '#15803d' };
    case 'checked_out': return { background: '#f1f5f9', color: '#475569' };
    case 'cancelled': case 'no_show': return { background: '#fee2e2', color: '#b91c1c' };
    default: return { background: '#fef3c7', color: '#b45309' };
  }
}

const s: Record<string, React.CSSProperties> = {
  page: { maxWidth: 640, margin: '0 auto', padding: '12px 14px 40px', color: '#0f172a' },
  header: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 },
  back: { background: '#f1f5f9', border: 'none', borderRadius: 8, width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#334155' },
  title: { display: 'flex', alignItems: 'center', gap: 8, fontSize: '1.15rem', fontWeight: 800 },
  sub: { fontSize: '0.82rem', color: '#64748b' },
  tabs: { display: 'flex', gap: 4, marginBottom: 14, background: '#eef2f7', border: '1px solid #e2e8f0', borderRadius: 9999, padding: 4 },
  tab: { flex: 1, border: '1px solid transparent', background: 'transparent', borderRadius: 9999, padding: '9px 0', fontWeight: 700, fontSize: '0.85rem', color: '#64748b', cursor: 'pointer', transition: 'all 0.15s ease' },
  tabActive: { background: '#2563eb', color: '#fff', borderColor: '#2563eb', boxShadow: '0 2px 8px rgba(37,99,235,0.35)' },
  error: { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', fontSize: '0.82rem', marginBottom: 12 },
  searchCard: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, marginBottom: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: '0.74rem', fontWeight: 700, color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: 4 },
  input: { width: '100%', boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 10px', fontSize: '0.9rem', color: '#0f172a' },
  primaryBtn: { gridColumn: '1 / -1', background: '#ff6b35', color: '#fff', border: 'none', borderRadius: 9, padding: '11px', fontWeight: 700, fontSize: '0.92rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 },
  note: { textAlign: 'center', color: '#94a3b8', padding: '28px 12px', fontSize: '0.9rem' },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  roomCard: { border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: '#fff', boxShadow: '0 1px 2px rgba(15,23,42,0.05)' },
  soldOut: { opacity: 0.7 },
  roomImg: { width: '100%', height: 150, objectFit: 'cover', display: 'block' },
  roomBody: { padding: '12px 14px' },
  roomName: { fontWeight: 800, fontSize: '1rem' },
  roomMeta: { fontSize: '0.8rem', color: '#64748b', marginTop: 2 },
  amenities: { fontSize: '0.78rem', color: '#475569', marginTop: 6 },
  priceRow: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 10 },
  price: { fontSize: '1.15rem', color: '#16a34a' },
  perNight: { fontSize: '0.72rem', color: '#94a3b8', marginLeft: 2 },
  totalLine: { fontSize: '0.76rem', color: '#64748b', marginTop: 2 },
  bookBtn: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontWeight: 700, cursor: 'pointer' },
  soldBadge: { background: '#f1f5f9', color: '#94a3b8', borderRadius: 8, padding: '8px 12px', fontWeight: 700, fontSize: '0.82rem' },
  fewLeft: { fontSize: '0.74rem', color: '#dc2626', marginTop: 6, fontWeight: 600 },
  confirmCard: { border: '1px solid #dcfce7', background: '#f0fdf4', borderRadius: 14, padding: '22px 18px', textAlign: 'center' },
  confirmIcon: { width: 56, height: 56, borderRadius: '50%', background: '#16a34a', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  confirmSub: { color: '#475569', fontSize: '0.86rem', margin: '0 0 14px' },
  confirmRow: { display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', padding: '5px 0', borderTop: '1px solid #dcfce7', color: '#334155' },
  stayCard: { border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff', padding: '12px 14px' },
  stayTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  stayMeta: { fontSize: '0.82rem', color: '#475569', marginTop: 4 },
  statusBadge: { borderRadius: 9999, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700 },
  code: { fontSize: '0.74rem', color: '#94a3b8', marginTop: 6 },
  cancelBtn: { background: '#fff', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, padding: '7px 14px', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' },
  serviceBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' },
  unreadDot: { position: 'absolute', top: -6, right: -6, minWidth: 18, height: 18, padding: '0 4px', borderRadius: 9999, background: '#ef4444', color: '#fff', fontSize: '0.65rem', fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 2px #fff' },
};

export default RoomBooking;
